import type { ButtonVariant, ModalProps } from '@mantine/core';
import { Button, Modal, Flex, ScrollArea } from '@mantine/core';
import classes from './Modals.module.css';
interface CustomModalProps extends ModalProps {
  isLoading?: boolean;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  primaryLabel?: string;
  secondaryLabel?: string;
  /**
   * If you provide a form ID, the modal will submit that form
   * when the primary action is triggered.
   */
  formToSubmit?: string;
  /**
   * Optional test ID for the modal container
   */
  testId?: string;
  /**
   * Optional test ID for the primary action button
   */
  primaryButtonTestId?: string;
  /**
   * Optional test ID for the secondary action button
   */
  secondaryButtonTestId?: string;
  /**
   * Disable the primary action button
   */
  primaryButtonDisabled?: boolean;
  /**
   * Color for the primary action button (defaults to 'tealBrand')
   */
  primaryButtonColor?: string;
  /**
   * Variant for the primary action button (defaults to 'filled')
   */
  primaryButtonVariant?: ButtonVariant;
  /**
   * Extra footer elements to render in the footer next to the buttons
   */
  footerElements?: React.ReactNode;
  /**
   * Make buttons take full width and center justify content
   */
  fullWidthButtons?: boolean;
}

const ModalComponent = ({
  // These props don't get passed to the Mantine Modal component.
  isLoading,
  onPrimaryAction,
  onSecondaryAction,
  primaryLabel,
  secondaryLabel,
  formToSubmit,
  testId,
  primaryButtonTestId,
  secondaryButtonTestId,
  primaryButtonDisabled,
  primaryButtonColor = 'tealBrand',
  primaryButtonVariant = 'filled',
  footerElements,
  fullWidthButtons,

  // Set modal defaults here.
  // Anything added to this list needs to be directly added
  // to the Modal component below - they won't be included in {...props }
  classNames = {},
  radius = 'lg',
  centered = true,
  withCloseButton = true,
  ...props
}: CustomModalProps) => {
  return (
    <Modal
      centered={centered}
      radius={radius}
      withCloseButton={withCloseButton}
      trapFocus
      data-testid={testId}
      // This makes the right border radii show up properly
      scrollAreaComponent={ScrollArea.Autosize}
      classNames={{
        root: classes.root,
        inner: classes.inner,
        content: classes.content,
        body: classes.body,
        close: classes.close,
        header: classes.header,
        title: classes.title,
        ...classNames, // Allow overriding of default classNames
      }}
      {...props}
    >
      {props.children}

      {onPrimaryAction || onSecondaryAction ? (
        <Flex
          className={`${classes.footer} ${fullWidthButtons ? classes.fullWidthButtons : ''}`}
        >
          {footerElements && (
            <Flex className={classes.footerElements}>{footerElements}</Flex>
          )}
          {onSecondaryAction && !isLoading && (
            <Button
              variant="outline"
              radius="md"
              type="reset"
              color="tealBrand"
              h="40px"
              size="compact-md"
              onClick={onSecondaryAction}
              className={classes.secondaryButton}
              data-testid={secondaryButtonTestId}
            >
              {secondaryLabel}
            </Button>
          )}
          {(onPrimaryAction || formToSubmit) && (
            <Button
              type="submit"
              radius="md"
              form={formToSubmit}
              color={primaryButtonColor}
              variant={primaryButtonVariant}
              h="40px"
              size="compact-md"
              onClick={onPrimaryAction}
              className={classes.primaryButton}
              loading={isLoading}
              disabled={primaryButtonDisabled}
              data-testid={primaryButtonTestId}
            >
              {primaryLabel}
            </Button>
          )}
        </Flex>
      ) : null}
    </Modal>
  );
};

export default ModalComponent;
